import React, { useEffect, useRef, useState } from "react";
import { CSSProperties } from "react";
import { colors, fonts, glyphs } from "../../Styles";
import { createPortal } from "react-dom";
import { createPopper } from "@popperjs/core";

// #region | Helper function
function mergeStyles(defaults: CSSProperties, overrides?: CSSProperties): CSSProperties {
  return { ...defaults, ...(overrides ?? {}) };
}
// #endregion

// #region | Tooltip positions
export const TooltipPositions = {
  top : "top",
  bottom: "bottom"
} as const;
// #endregion

interface TooltipStringProps {
  contentString: string | JSX.Element
  tooltipString?: string
  duplicatedStrings?: boolean
  overrides?: {
    contentstring?: CSSProperties
    tooltipString?: CSSProperties
    tooltipColor?: colors
    toolTipPosition?: typeof TooltipPositions[keyof typeof TooltipPositions];
    additionalOnMouseEnter?: () => void;
    additionalOnMouseLeave?: () => void;
  }
}
/**
 * @param contentString - string with hover effect
 * @param tooltipString - string shown when content string is hovered
 * @param duplicatedStrings - if the tooltip string is the same as content string use content string as tooltip string 
 * @param overrides - possibilities to change style on strings, tooltip position, and tooltip color.
 * @returns a tooltip component on content/text hover
 */ 
export default function TooltipString({tooltipString, contentString, duplicatedStrings=false, overrides }:TooltipStringProps) {
  const popperRef = useRef<HTMLDivElement | null>(null);
  const arrowRef = useRef<HTMLDivElement>(null);
  const referenceRef = useRef<HTMLDivElement | null>(null);
  const [popperReady, setPopperReady] = useState(false);

  const [showTooltip, setShowTooltip] = useState<boolean>(false)

  const _labelStyle = {...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary, ...fonts.fontWeight2, whiteSpace: "nowrap", textOverflow: "ellipsis", maxWidth: "100%", width: "fit-content", minHeight: "15px", overflow: "hidden"};

  // Styles
  const _contentStringOverrides = mergeStyles({..._labelStyle, color: colors.greyDarkPrimary, cursor: "default" }, overrides?.contentstring)
  const _tooltipStringOverrides = mergeStyles({...fonts.fontSize4, ...fonts.paragraph, color: colors.greyLightTertiary, cursor: "default" }, overrides?.tooltipString)
  const _toolTipPosition = overrides && overrides?.toolTipPosition ? overrides.toolTipPosition : TooltipPositions.top
  const _tooltipColor = overrides?.tooltipColor ? overrides?.tooltipColor : colors.greyDarkPrimary

  // #region | Glyph/Arrow position styles
  let _bottom = ""
  let _rotation = 0

  switch (overrides?.toolTipPosition) {
    case TooltipPositions.bottom:
      _bottom = "28px"
      _rotation = 270
      break;
    case TooltipPositions.top:
    default:
      _bottom = "-10px"
      _rotation = 90
      break;
  }
  // #endregion

  // Popper JS effect
  useEffect(() => {
    if (popperRef.current && referenceRef.current && arrowRef.current) {;
      const popper = createPopper(referenceRef.current, popperRef.current, {
        placement: _toolTipPosition,
        strategy: "fixed",
        modifiers: [
          { name: "offset", options: { offset: [0, 8] } },
          { name: "preventOverflow", options: { boundary: "viewport" } },
          { name: "flip", options: {fallbackPlacements: ["top", "bottom"]}},
          { name: "arrow", options: { element:  arrowRef.current } },
        ],
      });
      popper.update().then(() => setPopperReady(true));
      return () => popper.destroy();
    }
  }, [_toolTipPosition, showTooltip]);

  return ( <>
    <div
      ref={referenceRef}
      style={{ ..._contentStringOverrides, position: "relative" }} 
      onMouseEnter={tooltipString || duplicatedStrings ? () => { setShowTooltip(true); overrides?.additionalOnMouseEnter?.() } : undefined} 
      onMouseLeave={tooltipString || duplicatedStrings ? () => { setShowTooltip(false); overrides?.additionalOnMouseLeave?.() } : undefined}
    >
        {contentString}
    </div>
    {(tooltipString || duplicatedStrings === true) && createPortal(
      <div ref={popperRef} style={{
        backgroundColor: _tooltipColor,
        zIndex: "9999999999",
        borderRadius: '8px',
        boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
        opacity: showTooltip&&popperReady ? 1 : 0,
        visibility: showTooltip&&popperReady ? 'visible' : 'hidden',
        transition: 'opacity 0.2s ease, visibility 0.2s ease',
        pointerEvents: showTooltip&&popperReady ? 'auto' : 'none',
      }}>
        <div style={{..._tooltipStringOverrides, padding: "5px 10px"}}>{duplicatedStrings === true ? contentString : tooltipString}</div>
        <div ref={arrowRef} style={{ position: "absolute", bottom: _bottom, width: "15px", height: "15px", pointerEvents: "none" }} data-popper-arrow>
          <glyphs.ArrowSmall rotate={_rotation} fillColor={_tooltipColor} />
        </div>
      </div>, document.body)
    }
  </> )
}